import { useCallback, useContext } from "react";
import { Divider, NoSsr, Typography, styled } from "@mui/material";
import { EditorCtx } from "../../wysiwyg/EditorCtx";
import { useGoogleSSO, useMicrosoftSSO } from "../../dataAccess/SSO";
import GoogleIcon from "../../assets/svg/google-logo.svg";
import OutlookIcon from "../../assets/svg/outlook-logo.svg";
import { ThemeButton } from "./ThemeComponents/ThemeButton";
import { ProfileContext } from "../public/Profile";
import { iframe } from "../../helpers/environment";
import { openSSOPopup } from "../../app/ssoPopup";
import { OR_LABEL } from "../../../theme/defaultConstants";
import { useTranslation } from "../../helpers/i18n";
import { replaceBracesKey } from "../../helpers/formatter";

const StyledDivider = styled(Divider)(() => ({
  width: "100%",
  "&:before, &:after": {
    borderTopWidth: "2px",
    borderTopStyle: "dashed",
  },
}));

const Content = styled("div")(() => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexDirection: "column",
  gap: "1rem",
}));

const Main = styled("div")(() => ({
  display: "flex",
  flexDirection: "column",
  marginTop: "1.5rem",
  marginBottom: ".5rem",
  gap: "2rem",
}));

const StyledGoogleIcon = styled(GoogleIcon)(() => ({
  width: "1rem",
}));

const StyledOutlookIcon = styled(OutlookIcon)(() => ({
  width: "1rem",
  height: "1rem",
}));

const ButtonLabel = styled("span")(() => ({
  color: "inherit",
}));

interface Props {
  orLabel?: string;
}

const SSOIntegration = ({ orLabel }: Props) => {
  const { googleUrl, googleProvider } = useGoogleSSO();
  const { isEditor, messages, setCurrentMessage } = useContext(EditorCtx);
  const { setLoginOpen, setSignUpOpen } = useContext(ProfileContext);
  const { microsoftUrl, microsoftProvider } = useMicrosoftSSO();
  const { t } = useTranslation();

  const onClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>, url: string, provider: string) => {
      if (isEditor) {
        e.preventDefault();
        setCurrentMessage(messages.previewAlert);
        setLoginOpen(false);
        setSignUpOpen(false);
      } else if (iframe) {
        e.preventDefault();
        openSSOPopup(url, provider);
      }
    },
    [
      isEditor,
      messages.previewAlert,
      setCurrentMessage,
      setLoginOpen,
      setSignUpOpen,
    ],
  );

  if (!(googleUrl || microsoftUrl)) {
    return <></>;
  }

  return (
    <NoSsr>
      <Main>
        <Content>
          {googleUrl && (
            <ThemeButton
              href={iframe ? undefined : googleUrl}
              onClick={(e) => onClick(e, googleUrl, googleProvider)}
              fullWidth
              endIcon={<StyledGoogleIcon />}
              aria-label={replaceBracesKey(
                t("accessibility_sso_continue_with_provider"),
                { key: "provider", value: "GOOGLE" },
              )}
            >
              <ButtonLabel>GOOGLE</ButtonLabel>
            </ThemeButton>
          )}
          {microsoftUrl && (
            <ThemeButton
              customVariant="secondary"
              href={iframe ? undefined : microsoftUrl}
              onClick={(e) => onClick(e, microsoftUrl, microsoftProvider)}
              fullWidth
              endIcon={<StyledOutlookIcon />}
              aria-label={replaceBracesKey(
                t("accessibility_sso_continue_with_provider"),
                { key: "provider", value: "OUTLOOK" },
              )}
            >
              <ButtonLabel>OUTLOOK</ButtonLabel>
            </ThemeButton>
          )}
        </Content>
        <StyledDivider>
          <Typography component="span">{orLabel || OR_LABEL}</Typography>
        </StyledDivider>
      </Main>
    </NoSsr>
  );
};

export default SSOIntegration;
